import "../css/header.scss";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  FiArrowRight,
  FiChevronDown,
  FiHeart,
  FiMenu,
  FiSearch,
  FiShoppingBag,
  FiUser,
  FiX,
} from "react-icons/fi";
import { CartContext } from "./CartContext";
import productData from "../Datas/productData";
import siteConfig, { formatPrice } from "../Datas/siteConfig";

const shopLinks = [
  { label: "Shop all", to: "/shop" },
  { label: "Hats", to: "/shop?category=Hats" },
  { label: "Ready to wear", to: "/shop?category=Ready%20to%20wear" },
  { label: "Accessories", to: "/shop?category=Accessories" },
];

const moreLinks = [
  { label: "Our story", to: "/about" },
  { label: "Contact", to: "/contact" },
  { label: "Shipping", to: "/shipping" },
  { label: "FAQs", to: "/faqs" },
  { label: "Privacy", to: "/privacy" },
  { label: "Cookies", to: "/cookies" },
  { label: "Terms", to: "/terms" },
];

function Header() {
  const location = useLocation();
  const {
    cartItems = [],
    wishlist = [],
    toggleWishlist,
    isWishlisted,
  } = useContext(CartContext);
  const [openMenu, setOpenMenu] = useState(null);
  const [activePanel, setActivePanel] = useState(null);
  const [query, setQuery] = useState("");
  const [isScrolled, setIsScrolled] = useState(() =>
    typeof window === "undefined" ? false : window.scrollY > 24
  );
  const searchInputRef = useRef(null);
  const panelRef = useRef(null);
  const lastTriggerRef = useRef(null);

  const isHome = location.pathname === "/";
  const usesLightHeader =
    isHome && !isScrolled && !activePanel && openMenu === null;
  const cartCount = cartItems.reduce(
    (total, item) => total + Number(item.quantity || 1),
    0
  );

  const wishlistIds = useMemo(
    () =>
      wishlist
        .map((item) => Number(typeof item === "object" ? item.id : item))
        .filter(Number.isFinite),
    [wishlist]
  );

  const wishlistProducts = useMemo(
    () => productData.filter((product) => wishlistIds.includes(product.id)),
    [wishlistIds]
  );

  const searchResults = useMemo(() => {
    const normalizedQuery = query.trim().toLocaleLowerCase();

    if (!normalizedQuery) {
      return productData.filter((product) => product.featured).slice(0, 6);
    }

    return productData
      .filter((product) => {
        const searchableText = [
          product.name,
          product.category,
          product.description,
          ...(product.tags || []),
        ]
          .join(" ")
          .toLocaleLowerCase();

        return searchableText.includes(normalizedQuery);
      })
      .slice(0, 8);
  }, [query]);

  const productIsWishlisted = (productId) =>
    typeof isWishlisted === "function"
      ? isWishlisted(productId)
      : wishlistIds.includes(Number(productId));

  const handleWishlistToggle = (productId) => {
    if (typeof toggleWishlist === "function") {
      toggleWishlist(productId);
    }
  };

  const openPanel = (panelName, event) => {
    if (!activePanel) {
      lastTriggerRef.current = event?.currentTarget || null;
    }
    setOpenMenu(null);
    setActivePanel(panelName);
  };

  const closePanel = (restoreFocus = true) => {
    const focusTarget = restoreFocus ? lastTriggerRef.current : null;
    setActivePanel(null);
    setQuery("");

    if (focusTarget) {
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => focusTarget.focus());
      });
    }
  };

  const handleNavigation = () => {
    setOpenMenu(null);
    setActivePanel(null);
    setQuery("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 24);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setOpenMenu(null);
    setActivePanel(null);
    setQuery("");
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (!activePanel) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const backgroundElements = [
      document.querySelector(".skip-link"),
      document.querySelector(".site-header"),
      document.querySelector("main"),
      document.querySelector(".site-footer"),
    ].filter(Boolean);
    const backgroundState = backgroundElements.map((element) => ({
      element,
      inert: element.inert,
      ariaHidden: element.getAttribute("aria-hidden"),
    }));
    let backgroundDisabled = false;

    const focusTimer = window.requestAnimationFrame(() => {
      if (activePanel === "search") {
        searchInputRef.current?.focus();
      } else {
        panelRef.current?.focus();
      }

      backgroundElements.forEach((element) => {
        element.inert = true;
        element.setAttribute("aria-hidden", "true");
      });
      backgroundDisabled = true;
    });

    return () => {
      document.body.style.overflow = previousOverflow;
      window.cancelAnimationFrame(focusTimer);
      if (backgroundDisabled) {
        backgroundState.forEach(({ element, inert, ariaHidden }) => {
          element.inert = inert;
          if (ariaHidden === null) element.removeAttribute("aria-hidden");
          else element.setAttribute("aria-hidden", ariaHidden);
        });
      }
    };
  }, [activePanel]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Tab" && activePanel && panelRef.current) {
        const focusableElements = Array.from(
          panelRef.current.querySelectorAll(
            'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
          )
        ).filter((element) => element.getClientRects().length > 0);

        if (focusableElements.length > 0) {
          const firstElement = focusableElements[0];
          const lastElement = focusableElements[focusableElements.length - 1];

          if (
            event.shiftKey &&
            (document.activeElement === firstElement ||
              document.activeElement === panelRef.current)
          ) {
            event.preventDefault();
            lastElement.focus();
          } else if (
            !event.shiftKey &&
            document.activeElement === lastElement
          ) {
            event.preventDefault();
            firstElement.focus();
          }
        } else {
          event.preventDefault();
          panelRef.current.focus();
        }
      }

      if (event.key !== "Escape") {
        return;
      }

      if (activePanel) {
        closePanel();
      } else if (openMenu) {
        setOpenMenu(null);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  });

  return (
    <>
      <header
        className={
          "site-header" +
          (isHome ? " site-header--home" : "") +
          (usesLightHeader ? " site-header--light" : "") +
          (isScrolled ? " is-scrolled" : "")
        }
      >
        <div className="site-header__inner">
          <nav
            className="site-header__desktop-nav"
            aria-label="Main navigation"
          >
            <div
              className={
                "site-header__nav-group" +
                (openMenu === "shop" ? " is-open" : "")
              }
              onMouseEnter={() => setOpenMenu("shop")}
              onMouseLeave={() => setOpenMenu(null)}
            >
              <button
                className="site-header__nav-button"
                type="button"
                aria-expanded={openMenu === "shop"}
                aria-controls="shop-menu"
                onClick={() =>
                  setOpenMenu((current) => (current === "shop" ? null : "shop"))
                }
              >
                Shop <FiChevronDown aria-hidden="true" />
              </button>
              <div className="site-header__dropdown" id="shop-menu">
                <p>Explore the collection</p>
                {shopLinks.map((item) => (
                  <Link
                    key={item.label}
                    to={item.to}
                    onClick={handleNavigation}
                  >
                    {item.label}
                    <FiArrowRight aria-hidden="true" />
                  </Link>
                ))}
              </div>
            </div>

            <div
              className={
                "site-header__nav-group" +
                (openMenu === "more" ? " is-open" : "")
              }
              onMouseEnter={() => setOpenMenu("more")}
              onMouseLeave={() => setOpenMenu(null)}
            >
              <button
                className="site-header__nav-button"
                type="button"
                aria-expanded={openMenu === "more"}
                aria-controls="more-menu"
                onClick={() =>
                  setOpenMenu((current) => (current === "more" ? null : "more"))
                }
              >
                More <FiChevronDown aria-hidden="true" />
              </button>
              <div className="site-header__dropdown" id="more-menu">
                <p>Inside the studio</p>
                {moreLinks.map((item) => (
                  <Link
                    key={item.label}
                    to={item.to}
                    onClick={handleNavigation}
                  >
                    {item.label}
                    <FiArrowRight aria-hidden="true" />
                  </Link>
                ))}
              </div>
            </div>
          </nav>

          <button
            className="site-header__mobile-button site-header__menu-button"
            type="button"
            aria-label="Open menu"
            aria-expanded={activePanel === "menu"}
            aria-controls="mobile-menu"
            onClick={(event) => openPanel("menu", event)}
          >
            <FiMenu aria-hidden="true" />
          </button>

          <Link
            className="site-header__wordmark"
            to="/"
            aria-label={siteConfig.brandName + " home"}
            onClick={handleNavigation}
          >
            <img
              src="/head/reamflora-wordmark.svg"
              alt={siteConfig.brandName}
            />
          </Link>

          <div className="site-header__actions">
            <button
              className="site-header__action site-header__desktop-action"
              type="button"
              aria-label="Search products"
              aria-expanded={activePanel === "search"}
              onClick={(event) => openPanel("search", event)}
            >
              <FiSearch aria-hidden="true" />
              <span>Search</span>
            </button>

            <button
              className="site-header__action site-header__desktop-action"
              type="button"
              aria-label={"Wishlist, " + wishlistIds.length + " items"}
              aria-expanded={activePanel === "wishlist"}
              onClick={(event) => openPanel("wishlist", event)}
            >
              <FiHeart aria-hidden="true" />
              <span className="site-header__action-label">Wishlist</span>
              {wishlistIds.length > 0 && (
                <span className="site-header__count" aria-hidden="true">
                  {wishlistIds.length}
                </span>
              )}
            </button>
            <button
              className="site-header__action site-header__mobile-search"
              type="button"
              aria-label="Search products"
              aria-expanded={activePanel === "search"}
              onClick={(event) => openPanel("search", event)}
            >
              <FiSearch aria-hidden="true" />
            </button>
            <Link
              className="site-header__action site-header__cart"
              to="/cartpage"
              aria-label={"Shopping bag, " + cartCount + " items"}
              onClick={handleNavigation}
            >
              <FiShoppingBag aria-hidden="true" />
              <span className="site-header__cart-label">Bag</span>
              <span className="site-header__cart-count" aria-hidden="true">
                ({cartCount})
              </span>
            </Link>
          </div>
        </div>
      </header>

      {activePanel === "search" && (
        <div
          className="header-overlay header-overlay--search"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              closePanel();
            }
          }}
        >
          <section
            ref={panelRef}
            className="header-search"
            role="dialog"
            aria-modal="true"
            aria-labelledby="search-title"
            tabIndex="-1"
          >
            <div className="header-search__topline">
              <p id="search-title">Search the collection</p>
              <button
                type="button"
                aria-label="Close search"
                onClick={closePanel}
              >
                <FiX aria-hidden="true" />
              </button>
            </div>
            <label className="header-search__field">
              <span className="sr-only">Search products</span>
              <FiSearch aria-hidden="true" />
              <input
                ref={searchInputRef}
                type="search"
                value={query}
                placeholder="What are you looking for?"
                autoComplete="off"
                onChange={(event) => setQuery(event.target.value)}
              />
              {query && (
                <button
                  type="button"
                  aria-label="Clear search"
                  onClick={() => setQuery("")}
                >
                  Clear
                </button>
              )}
            </label>

            <div className="header-search__results" aria-live="polite">
              <div className="header-search__results-heading">
                <span>
                  {query.trim() ? "Search results" : "Suggested pieces"}
                </span>
                <span>{searchResults.length} items</span>
              </div>
              {searchResults.length > 0 ? (
                <div className="header-search__grid">
                  {searchResults.map((product) => (
                    <article className="header-search__card" key={product.id}>
                      <Link
                        to={"/shopDetails/" + product.id}
                        onClick={handleNavigation}
                      >
                        <img src={product.image} alt="" loading="lazy" />
                        <span>
                          <strong>{product.name}</strong>
                          <small>{formatPrice(product.price)}</small>
                        </span>
                      </Link>
                      <button
                        className={
                          productIsWishlisted(product.id) ? "is-active" : ""
                        }
                        type="button"
                        aria-label={
                          (productIsWishlisted(product.id)
                            ? "Remove "
                            : "Add ") +
                          product.name +
                          (productIsWishlisted(product.id)
                            ? " from wishlist"
                            : " to wishlist")
                        }
                        aria-pressed={productIsWishlisted(product.id)}
                        onClick={() => handleWishlistToggle(product.id)}
                      >
                        <FiHeart aria-hidden="true" />
                      </button>
                    </article>
                  ))}
                </div>
              ) : (
                <div className="header-search__empty">
                  <p>No pieces match “{query.trim()}”.</p>
                  <Link to="/shop" onClick={handleNavigation}>
                    Discover the full collection
                  </Link>
                </div>
              )}
            </div>
          </section>
        </div>
      )}

      {(activePanel === "menu" ||
        activePanel === "wishlist" ||
        activePanel === "account") && (
        <div
          className="header-overlay header-overlay--drawer"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              closePanel();
            }
          }}
        >
          <div
            ref={panelRef}
            className="header-drawer"
            id={activePanel === "menu" ? "mobile-menu" : undefined}
            role="dialog"
            aria-modal="true"
            aria-labelledby={activePanel + "-panel-title"}
            tabIndex="-1"
          >
            <div className="header-drawer__head">
              <p id={activePanel + "-panel-title"}>
                {activePanel === "menu" && "Menu"}
                {activePanel === "wishlist" && "Wishlist"}
                {activePanel === "account" && "Account"}
              </p>
              <button
                type="button"
                aria-label="Close panel"
                onClick={closePanel}
              >
                <FiX aria-hidden="true" />
              </button>
            </div>

            {activePanel === "menu" && (
              <div className="header-drawer__navigation">
                <div className="header-drawer__primary-links">
                  <Link to="/shop" onClick={handleNavigation}>
                    Shop all <FiArrowRight aria-hidden="true" />
                  </Link>
                  <Link to="/about" onClick={handleNavigation}>
                    Our story <FiArrowRight aria-hidden="true" />
                  </Link>
                </div>
                <div className="header-drawer__link-group">
                  <p>Shop</p>
                  {shopLinks.slice(1).map((item) => (
                    <Link
                      key={item.label}
                      to={item.to}
                      onClick={handleNavigation}
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>
                <div className="header-drawer__link-group">
                  <p>More</p>
                  {moreLinks.map((item) => (
                    <Link
                      key={item.label}
                      to={item.to}
                      onClick={handleNavigation}
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>
                <div className="header-drawer__utilities">
                  <button
                    type="button"
                    onClick={(event) => openPanel("wishlist", event)}
                  >
                    <FiHeart aria-hidden="true" /> Wishlist (
                    {wishlistIds.length})
                  </button>
                  <button
                    type="button"
                    onClick={(event) => openPanel("account", event)}
                  >
                    <FiUser aria-hidden="true" /> Account
                  </button>
                </div>
              </div>
            )}

            {activePanel === "wishlist" && (
              <div className="header-drawer__wishlist">
                {wishlistProducts.length > 0 ? (
                  <>
                    <div className="header-drawer__product-list">
                      {wishlistProducts.map((product) => (
                        <article key={product.id}>
                          <Link
                            to={"/shopDetails/" + product.id}
                            onClick={handleNavigation}
                          >
                            <img src={product.image} alt="" loading="lazy" />
                            <span>
                              <strong>{product.name}</strong>
                              <small>{formatPrice(product.price)}</small>
                            </span>
                          </Link>
                          <button
                            type="button"
                            aria-label={
                              "Remove " + product.name + " from wishlist"
                            }
                            onClick={() => handleWishlistToggle(product.id)}
                          >
                            Remove
                          </button>
                        </article>
                      ))}
                    </div>
                    <Link
                      className="header-drawer__cta"
                      to="/shop"
                      onClick={handleNavigation}
                    >
                      Continue shopping
                    </Link>
                  </>
                ) : (
                  <div className="header-drawer__empty">
                    <FiHeart aria-hidden="true" />
                    <p>Your wishlist is waiting for something special.</p>
                    <Link to="/shop" onClick={handleNavigation}>
                      Explore the collection
                    </Link>
                  </div>
                )}
              </div>
            )}

            {activePanel === "account" && (
              <div className="header-drawer__account">
                <p>
                  Need help with an order or finding the right piece? Our studio
                  team is here to help.
                </p>
                <a href={"mailto:" + siteConfig.contact.customerServiceEmail}>
                  {siteConfig.contact.customerServiceEmail}
                </a>
                <Link to="/contact" onClick={handleNavigation}>
                  Contact customer care
                </Link>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

export default Header;
